import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Project, ProjectStatus } from '@/types/project';
import { RevenueType } from '@/types/crm';
import { formatCurrency } from './utils';

interface ProjectHeaderProps {
  project: Project | null | undefined;
  projectName: string;
  projectStatus: ProjectStatus;
  revenue: number;
  revenueType?: RevenueType;
  hasUnsavedChanges: boolean;
  onBackToList: () => void;
  onSave: () => void;
  isSaveDisabled: boolean;
  isSaving?: boolean;
}

export function ProjectHeader({
  project,
  projectName,
  projectStatus,
  revenue,
  revenueType,
  hasUnsavedChanges,
  onBackToList,
  onSave,
  isSaveDisabled,
  isSaving = false,
}: ProjectHeaderProps) {
  const getStatusVariant = (status: ProjectStatus) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'prospective':
        return 'secondary';
      case 'inactive':
        return 'outline';
      default:
        return 'default';
    }
  };

  return (
    <div className="sticky top-0 z-20 bg-background border-b pb-3 md:pb-4 mb-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center sm:gap-4">
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBackToList} className="shrink-0">
            <ArrowLeft className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Back</span>
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg md:text-xl lg:text-2xl font-semibold truncate font-serif">
              {projectName || (project ? 'Edit Project' : 'New Project')}
            </h1>
            <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
              <Badge variant={getStatusVariant(projectStatus)} className="text-xs">
                {projectStatus}
              </Badge>
              <span className="font-serif">
                {revenueType === 'variable'
                  ? 'Variable'
                  : revenueType === 'one_off'
                    ? 'One-Off'
                    : `${formatCurrency(revenue)}/mo`}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          {hasUnsavedChanges && (
            <Badge variant="outline" className="text-warning border-warning text-xs">
              Unsaved
            </Badge>
          )}
          <Button
            onClick={onSave}
            className="bg-primary hover:bg-primary/90"
            disabled={isSaveDisabled || isSaving}
          >
            <Save className={`h-4 w-4 md:mr-2 ${isSaving ? 'animate-spin' : ''}`} />
            <span className="hidden md:inline">{isSaving ? 'Saving...' : (project ? 'Update' : 'Save')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
